/**
 * The hourly earnings guarantee (W10-MISSIONS-GUARANTEE, D492, ADR-0108; lever L4) inside Admin › Finanças › Ofertas de incentivo: the governed bounds, and the form that PROPOSES a
 * guarantee (OFF unless the market publishes `incentives.guaranteePolicy`). A DIFFERENT operator approves it in the offers table above - the approval reserves its budget in the
 * DRIVER envelope - and either may end it there. Money is typed through the design-system `MoneyInput` in the policy's currency; every code shown goes through the vocabulary.
 */
import { type ReactElement, useState } from 'react';
import type { ActiveIncentivesListView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Field, FormRow, MoneyInput, Notice, Panel, Row, Select, Stack, Text, TextInput } from '@vello/web-theme';
import { EMPTY_GUARANTEE_DRAFT, GUARANTEE_START_CHOICES, bpToPercentText, guaranteeDraftIssue, guaranteeDurations, guaranteeProposalBody, percentTextToBp, policyStateTone, type GuaranteeDraft } from '../../src/index';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

export function GuaranteeOffersPanel(props: {
  readonly view: ActiveIncentivesListView;
  readonly market: string;
  readonly mayManage: boolean;
  readonly busy: boolean;
  readonly submit: (body: ReturnType<typeof guaranteeProposalBody>) => Promise<boolean>;
}): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [draft, setDraft] = useState<GuaranteeDraft>(EMPTY_GUARANTEE_DRAFT);
  const [acceptanceText, setAcceptanceText] = useState('');
  const policy = props.view.guaranteePolicy;
  if (policy === undefined) return null;
  const open = policy.state === 'ACTIVE' && props.view.fund.state === 'ACTIVE' && policy.currency !== null && isIso4217Currency(policy.currency);
  const currency = policy.currency ?? '';
  const money = (m: { readonly amountMinor: string; readonly currency: string } | undefined): string =>
    m === undefined || !/^\d+$/.test(m.amountMinor) || !isIso4217Currency(m.currency) ? '—' : formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });
  const issue = open ? guaranteeDraftIssue(draft, policy, props.view.proposalReasons) : undefined;
  const durations = guaranteeDurations(policy.maxIncentiveHours ?? 0);

  const send = async (): Promise<void> => {
    if (issue !== undefined || !open) return;
    if (await props.submit(guaranteeProposalBody(props.market, draft, new Date().toISOString()))) {
      setDraft(EMPTY_GUARANTEE_DRAFT);
      setAcceptanceText('');
    }
  };

  return (
    <Panel>
      <Stack gap={3}>
        <h3>{t('guarantee.title')}</h3>
        <Text role="caption" tone="tertiary">{t('guarantee.subtitle')}</Text>
        <Row gap={2} align="center" wrap>
          <Text role="caption" tone="tertiary">{t('guarantee.policy')}</Text>
          <Badge tone={policyStateTone(policy.state)}>{v('incentive_fund.policy_state', policy.state)}</Badge>
          <Text role="caption" tone="tertiary">{t('offers.policy.version', { version: policy.policyVersion })}</Text>
        </Row>
        {!open ? (
          <Notice tone="warning">{t('guarantee.needs')}</Notice>
        ) : (
          <Text role="caption" tone="tertiary">
            <span className="tnum">
              {t('guarantee.bounds', {
                rate: money(policy.maxGuaranteePerHour), day: money(policy.maxTopUpPerDriverPerDay), hours: policy.maxIncentiveHours ?? 0, window: policy.settlementWindowHours ?? 0,
                minutes: policy.minOnlineMinutesFloor ?? 0, acceptance: bpToPercentText(policy.minAcceptanceBpFloor ?? 0),
              })}
            </span>
          </Text>
        )}
        {props.mayManage && open && (
          <>
            <FormRow action={<Button type="button" disabled={issue !== undefined || props.busy} onClick={() => void send()}>{t('guarantee.submit')}</Button>}>
              <MoneyInput id="guarantee-rate" label={t('guarantee.rate')} currency={currency} locale={lang} valueMinor={draft.rateMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, rateMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
              <Field label={t('guarantee.minutes')} htmlFor="guarantee-minutes" hint={t('guarantee.minutes.hint', { minutes: policy.minOnlineMinutesFloor ?? 0 })}>
                <TextInput id="guarantee-minutes" inputMode="numeric" maxLength={2} value={String(draft.minOnlineMinutes)} onChange={(ev) => setDraft((d) => ({ ...d, minOnlineMinutes: Number(ev.target.value.replace(/\D/g, '')) || 0 }))} />
              </Field>
              <Field label={t('guarantee.acceptance')} htmlFor="guarantee-acceptance" hint={t('guarantee.acceptance.hint', { percent: bpToPercentText(policy.minAcceptanceBpFloor ?? 0) })}>
                <TextInput
                  id="guarantee-acceptance"
                  inputMode="decimal"
                  maxLength={6}
                  value={acceptanceText}
                  onChange={(ev) => {
                    setAcceptanceText(ev.target.value);
                    setDraft((d) => ({ ...d, minAcceptanceBp: percentTextToBp(ev.target.value) ?? -1 }));
                  }}
                />
              </Field>
              <MoneyInput id="guarantee-budget" label={t('guarantee.budget')} currency={currency} locale={lang} valueMinor={draft.budgetMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, budgetMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
              <Field label={t('guarantee.start')} htmlFor="guarantee-start">
                <Select id="guarantee-start" value={draft.startInHours === undefined ? '' : String(draft.startInHours)} onChange={(ev) => setDraft((d) => ({ ...d, startInHours: ev.target.value === '' ? undefined : Number(ev.target.value) }))}>
                  <option value="">{t('guarantee.start.choose')}</option>
                  {GUARANTEE_START_CHOICES.map((h) => (
                    <option key={h} value={String(h)}>{h === 0 ? t('guarantee.start.nextHour') : t('guarantee.start.in', { hours: h })}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('guarantee.duration')} htmlFor="guarantee-duration">
                <Select id="guarantee-duration" value={draft.durationHours === undefined ? '' : String(draft.durationHours)} onChange={(ev) => setDraft((d) => ({ ...d, durationHours: ev.target.value === '' ? undefined : Number(ev.target.value) }))}>
                  <option value="">{t('offers.propose.window.choose')}</option>
                  {durations.map((h) => (
                    <option key={h} value={String(h)}>{t('offers.propose.window.hours', { hours: h })}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('offers.propose.reason')} htmlFor="guarantee-reason">
                <Select id="guarantee-reason" value={draft.reasonCode} onChange={(ev) => setDraft((d) => ({ ...d, reasonCode: ev.target.value }))}>
                  <option value="">{t('offers.propose.reason.choose')}</option>
                  {props.view.proposalReasons.map((r) => (
                    <option key={r} value={r}>{v('active_incentive.reason', r)}</option>
                  ))}
                </Select>
              </Field>
            </FormRow>
            {issue !== undefined && draft !== EMPTY_GUARANTEE_DRAFT && <Text role="caption" tone="tertiary">{t(`guarantee.issue.${issue}`)}</Text>}
          </>
        )}
      </Stack>
    </Panel>
  );
}
